import React, { useMemo, useCallback } from 'react';
import { classNames } from '../../utils/classNames';
import { OptionsMenu } from '../OptionsMenu/OptionsMenu';
import { AttachmentPreviewPopover } from '../AttachmentPreviewPopover/AttachmentPreviewPopover';
import type { MenuOption } from '../OptionsMenu/OptionsMenu.types';
import { AttachmentIcon, CloseIcon, PlusIcon, SendIcon } from '../Icons';
import type { InputActionBarProps } from './InputActionBar.types';
import { hasActionBar } from '../../utils/actionBarUtils';
import './InputActionBar.css';

function normalizeMenuOption(option: string | MenuOption): MenuOption {
  if (typeof option === 'string') {
    return { id: option.toLowerCase(), label: option };
  }
  return option;
}

export function InputActionBar({
  showUpload,
  showPlusMenu,
  showSend,
  showCancel,
  onUploadClick,
  attachmentCount = 0,
  onBadgeClick,
  uploadAreaRef,
  attachments,
  previewOpen,
  onClosePreview,
  onRemoveAttachment,
  onAttachmentFileClick,
  onCancelClick,
  attachmentBadgeColor,
  plusMenuOptions = [],
  onPlusMenuSelect,
  onSendClick,
  uploadIcon,
  plusIcon,
  sendIcon,
  cancelIcon,
  layout,
  className,
  style
}: InputActionBarProps): React.ReactElement | null {
  const hasAnyAction = hasActionBar({
    showUpload,
    showPlusMenu,
    showSend,
    showCancel
  });

  const normalizedOptions = useMemo(
    () => plusMenuOptions.map(normalizeMenuOption),
    [plusMenuOptions]
  );

  const handleUploadClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      onUploadClick?.();
    },
    [onUploadClick]
  );

  const handleBadgeClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onBadgeClick?.();
    },
    [onBadgeClick]
  );

  const handleSendClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      onSendClick?.();
    },
    [onSendClick]
  );

  const handleCancelClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      onCancelClick?.();
    },
    [onCancelClick]
  );

  if (!hasAnyAction) {
    return null;
  }

  const layoutModifier =
    layout === 'LEFT'
      ? 'input-action-bar--layout-left'
      : layout === 'RIGHT'
      ? 'input-action-bar--layout-right'
      : null;

  const leftContent = (
    <>
      {showUpload && (
        <div
          className="input-action-bar__upload-wrap"
          ref={uploadAreaRef as React.RefObject<HTMLDivElement>}
        >
          <button
            type="button"
            className="input-action-bar__button"
            onClick={handleUploadClick}
            aria-label="Upload files"
            title="Upload files"
          >
            {uploadIcon ?? <AttachmentIcon />}
          </button>
          {attachmentCount > 0 && (
            <button
              type="button"
              className={classNames(
                'input-action-bar__badge',
                attachmentBadgeColor && 'input-action-bar__badge--custom'
              )}
              style={
                attachmentBadgeColor
                  ? { background: attachmentBadgeColor }
                  : undefined
              }
              onClick={handleBadgeClick}
              aria-label={`${attachmentCount} file(s) attached; view list`}
              title="View attached files"
            >
              {attachmentCount > 99 ? '99+' : attachmentCount}
            </button>
          )}
          {showUpload && previewOpen && onClosePreview && (
            <AttachmentPreviewPopover
              attachments={attachments ?? []}
              open={true}
              onClose={onClosePreview}
              onRemove={onRemoveAttachment ?? (() => {})}
              onFileClick={onAttachmentFileClick}
            />
          )}
        </div>
      )}
      {showPlusMenu && (
        <OptionsMenu
          options={normalizedOptions}
          onOptionClick={onPlusMenuSelect ?? (() => {})}
          className="input-action-bar__plus-menu"
          triggerIcon={plusIcon ?? <PlusIcon />}
        />
      )}
    </>
  );

  const cancelButton = showCancel && (
    <button
      type="button"
      className={classNames(
        'input-action-bar__button',
        'input-action-bar__button--cancel'
      )}
      onClick={handleCancelClick}
      aria-label="Cancel"
      title="Cancel"
    >
      {cancelIcon ?? <CloseIcon />}
    </button>
  );

  const sendButton = showSend && (
    <button
      type="button"
      className={classNames(
        'input-action-bar__button',
        'input-action-bar__button--send'
      )}
      onClick={handleSendClick}
      aria-label="Send"
      title="Send"
    >
      {sendIcon ?? <SendIcon />}
    </button>
  );

  const rightContent = (
    <>
      {cancelButton}
      {sendButton}
    </>
  );

  const isAllLeft = layout === 'LEFT';
  const isAllRight = layout === 'RIGHT';

  return (
    <div
      className={classNames('input-action-bar', layoutModifier, className)}
      style={style}
      role="toolbar"
      aria-label="Comment actions"
      tabIndex={0}
    >
      <div className="input-action-bar__left">
        {!isAllRight && leftContent}
        {isAllLeft && rightContent}
      </div>
      <div className="input-action-bar__right">
        {isAllRight && leftContent}
        {!isAllLeft && rightContent}
      </div>
    </div>
  );
}

InputActionBar.displayName = 'InputActionBar';
